import React from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'October 9, 2026';
const COMPANY = 'Idaho Coders LLC';
const ADDRESS_LINE1 = '800 W Main St Suite 1460';
const ADDRESS_LINE2 = 'Boise, ID 83702';
const CONTACT_EMAIL = 'hello@classroom.bingo';

export default function PrivacyPage() {
  return (
    <div style={sty.page}>
      <div style={sty.inner}>
        <div style={sty.backRow}>
          <Link to="/" style={sty.back}>← Back to Classroom Bingo</Link>
        </div>

        <h1 style={sty.h1}>Privacy Policy</h1>
        <p style={sty.meta}>Effective: {EFFECTIVE_DATE} &nbsp;·&nbsp; {COMPANY}</p>

        <Section title="1. Who We Are">
          <p>
            Classroom Bingo is operated by <strong>{COMPANY}</strong>.
          </p>
          <address style={sty.address}>
            {COMPANY}<br />
            {ADDRESS_LINE1}<br />
            {ADDRESS_LINE2}
          </address>
          <p>
            Contact:{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={sty.link}>{CONTACT_EMAIL}</a>
          </p>
        </Section>

        <Section title="2. What We Collect — Teachers">
          <p>
            A teacher who arrives through a Teachers Pay Teachers link can use a lightweight
            account, which stores only a classroom name and the novels they unlocked. A full
            account adds an email address and password. Depending on the account, we collect:
          </p>
          <table style={sty.table}>
            <thead>
              <tr>
                <th style={sty.th}>Data</th>
                <th style={sty.th}>Purpose</th>
                <th style={sty.th}>Stored where</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={sty.td}>Email address</td>
                <td style={sty.td}>Login, password reset, transactional emails</td>
                <td style={sty.td}>Platform database</td>
              </tr>
              <tr>
                <td style={sty.td}>Password (hashed)</td>
                <td style={sty.td}>Authentication — never stored in plain text</td>
                <td style={sty.td}>Platform database</td>
              </tr>
              <tr>
                <td style={sty.td}>Classroom name</td>
                <td style={sty.td}>Displayed on dashboard and student bingo boards</td>
                <td style={sty.td}>Platform database</td>
              </tr>
              <tr>
                <td style={sty.td}>Stripe customer ID</td>
                <td style={sty.td}>Subscription and payment processing</td>
                <td style={sty.td}>Platform database + Stripe</td>
              </tr>
              <tr>
                <td style={sty.td}>Custom vocabulary lists</td>
                <td style={sty.td}>Subscriber-created content used in games</td>
                <td style={sty.td}>Platform database</td>
              </tr>
              <tr>
                <td style={sty.td}>Novels unlocked through Teachers Pay Teachers, and when</td>
                <td style={sty.td}>Permanent access to purchased novels</td>
                <td style={sty.td}>Platform database</td>
              </tr>
              <tr>
                <td style={sty.td}>Subscription status and renewal date</td>
                <td style={sty.td}>Access to the full library, renewal notices</td>
                <td style={sty.td}>Platform database + Stripe</td>
              </tr>
              <tr>
                <td style={sty.td}>Date you agreed to the Terms and this policy</td>
                <td style={sty.td}>Record of consent</td>
                <td style={sty.td}>Platform database</td>
              </tr>
              <tr>
                <td style={sty.td}>Record of renewal reminders sent</td>
                <td style={sty.td}>Avoiding duplicate reminder emails</td>
                <td style={sty.td}>Platform database</td>
              </tr>
              <tr>
                <td style={sty.td}>Password-reset and email-change requests (including a pending new email address)</td>
                <td style={sty.td}>Confirming the request; links are stored only in hashed form and expire after 1 hour (password reset) or 24 hours (email change)</td>
                <td style={sty.td}>Platform database</td>
              </tr>
            </tbody>
          </table>
        </Section>

        <Section title="3. What We Do Not Collect — Teachers">
          <ul style={sty.list}>
            <li><strong>Teacher name</strong> — not collected at signup or anywhere in the platform.</li>
            <li><strong>School name</strong> — not requested by the platform.</li>
            <li>
              <strong>Payment and billing details</strong> — card details and the billing address
              entered at checkout are collected and held by Stripe and never stored on our servers.
            </li>
          </ul>
        </Section>

        <Section title="4. What We Collect — Students">
          <p>
            Students do not create accounts. To join a game, a student enters only a display
            name, such as a first name or a first name and last initial. <strong>No email
            address, password, age, or other personal information is collected from
            students.</strong>
          </p>
          <p>The following student data exists only in memory for the duration of a game session:</p>
          <ul style={sty.list}>
            <li>Display name chosen by the student (shown to the teacher on the class roster)</li>
            <li>Bingo board layout (randomized vocabulary words)</li>
            <li>Marked squares during the game</li>
          </ul>
          <p>
            All of this data is discarded when the teacher ends the game, when the teacher leaves
            and doesn't return within 5 minutes, or at the latest 8 hours after the game starts.
            None of it is written to a database.
          </p>
        </Section>

        <Section title="5. COPPA and FERPA">
          <p>
            We are aware that the Service is used in classrooms that may include children under 13.
            Because we collect no persistent personal data from students, and because students
            interact with the platform under a teacher's supervision, we believe the Service is
            designed consistent with the Children's Online Privacy Protection Act (COPPA) and the
            Family Educational Rights and Privacy Act (FERPA).
          </p>
          <p>
            Teachers are responsible for using the Service in compliance with their school or
            district's privacy policies and any applicable parental consent requirements.
          </p>
        </Section>

        <Section title="6. Cookies and Session Data">
          <p>
            We use a single session cookie (<code style={sty.code}>cb_session</code>) to keep
            teachers logged in. A full account stays signed in for up to 30 days; a lightweight
            account's session ends when the browser is closed. Session records are stored in our
            database and removed when they expire.
          </p>
          <p>
            While a teacher is hosting a game, their browser's local storage keeps that game's
            room code so they can rejoin it after a refresh. It is cleared when the game ends.
          </p>
          <p>
            No advertising cookies or third-party tracking scripts are used on any page of the
            Service, including all student-facing pages.
          </p>
        </Section>

        <Section title="7. How We Share Data">
          <p>We do not sell or rent your data. Limited sharing occurs only as follows:</p>
          <ul style={sty.list}>
            <li>
              <strong>Stripe</strong> — your email address and a Stripe customer ID are shared
              with Stripe solely for subscription payment processing. Stripe's own{' '}
              <a href="https://stripe.com/privacy" style={sty.link}>privacy policy</a> governs
              their handling of this data.
            </li>
            <li>
              <strong>Transactional email provider</strong> — your email address is shared with
              our email delivery provider (e.g., Resend) solely to send platform-triggered
              messages such as password reset links, receipts and renewal reminders.
            </li>
            <li>
              <strong>Hosting provider</strong> — the platform's servers and database are hosted
              by Railway, which stores and processes data on our behalf and for no other purpose.
            </li>
            <li>
              <strong>Teachers Pay Teachers</strong> — no personal data from the platform is
              shared with TpT. The relationship ends at the point of purchase on TpT.
            </li>
          </ul>
          <p>
            We do not share any teacher or student data with advertising platforms or analytics
            services.
          </p>
        </Section>

        <Section title="8. Data Retention and Deletion">
          <ul style={sty.list}>
            <li>
              <strong>Active accounts:</strong> Teacher data is retained for the life of the account.
            </li>
            <li>
              <strong>Deleted accounts:</strong> Email, password hash, classroom name, Stripe
              customer ID, subscription records and TpT unlocks are permanently deleted
              immediately upon account deletion. Any active subscription is cancelled and your
              customer record is deleted from Stripe. Custom vocabulary lists are disconnected
              from the account and kept under a reference number, which is included in the
              deletion confirmation email, for up to 2 years so they can be restored on request.
              They are then purged.
            </li>
            <li>
              <strong>Game data:</strong> All game session data is discarded when the game ends,
              or at the latest 8 hours after it starts.
            </li>
            <li>
              <strong>IP addresses:</strong> Used briefly, in memory only, to limit repeated login
              and join attempts. They are never stored.
            </li>
          </ul>
          <p>
            To request deletion of your account and data, use the Account Settings page or email
            us at {CONTACT_EMAIL}.
          </p>
        </Section>

        <Section title="9. Your Rights">
          <p>
            Depending on your location, you may have rights to access, correct, or delete your
            personal data. To exercise these rights, contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={sty.link}>{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <Section title="10. Security">
          <p>
            Passwords are stored as secure hashes using bcrypt. Data is transmitted over HTTPS.
            We take reasonable measures to protect teacher data but cannot guarantee absolute
            security of any system connected to the internet.
          </p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material
            changes by posting the revised policy on this page and updating the effective date
            above. Continued use of the Service after changes take effect constitutes acceptance
            of the revised policy.
          </p>
        </Section>

        <Section title="12. Contact Us">
          <p>Privacy questions or requests:</p>
          <address style={sty.address}>
            {COMPANY}<br />
            {ADDRESS_LINE1}<br />
            {ADDRESS_LINE2}<br />
            <a href={`mailto:${CONTACT_EMAIL}`} style={sty.link}>{CONTACT_EMAIL}</a>
          </address>
        </Section>

        <div style={sty.footer}>
          <Link to="/terms" style={sty.link}>Terms of Service</Link>
          {' · '}
          <Link to="/" style={sty.link}>Back to Classroom Bingo</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={sty.section}>
      <h2 style={sty.h2}>{title}</h2>
      {children}
    </section>
  );
}

const sty = {
  page: {
    minHeight: '100vh', backgroundColor: '#f5f3ef',
    fontFamily: 'system-ui, sans-serif', color: '#1a1a2e',
  },
  inner: {
    maxWidth: '720px', margin: '0 auto', padding: '40px 24px 64px',
  },
  backRow: { marginBottom: '28px' },
  back: { fontSize: '14px', color: '#6b6b7a', textDecoration: 'none' },
  h1: {
    fontSize: '32px', fontWeight: '800', color: '#1a1a2e',
    margin: '0 0 6px', letterSpacing: '-0.3px',
  },
  meta: { fontSize: '13px', color: '#9ca3af', margin: '0 0 36px' },
  section: { marginBottom: '32px' },
  h2: {
    fontSize: '17px', fontWeight: '700', color: '#1a1a2e',
    margin: '0 0 10px', paddingBottom: '6px',
    borderBottom: '1px solid #e5e0d5',
  },
  address: {
    fontStyle: 'normal', lineHeight: '1.7', fontSize: '14px',
    backgroundColor: '#fff', border: '1px solid #e5e0d5',
    borderRadius: '8px', padding: '12px 16px',
    display: 'inline-block', margin: '10px 0',
  },
  table: {
    width: '100%', borderCollapse: 'collapse',
    fontSize: '14px', margin: '12px 0',
  },
  th: {
    textAlign: 'left', padding: '8px 12px',
    backgroundColor: '#1a1a2e', color: '#fff',
    fontWeight: '600', fontSize: '12px',
  },
  td: {
    padding: '8px 12px', borderBottom: '1px solid #e5e0d5',
    verticalAlign: 'top', lineHeight: '1.5',
  },
  list: {
    paddingLeft: '20px', lineHeight: '1.8', fontSize: '15px', color: '#374151',
  },
  code: {
    fontFamily: 'monospace', backgroundColor: '#f3f4f6',
    padding: '1px 5px', borderRadius: '3px', fontSize: '13px',
  },
  link: { color: '#1a1a2e', fontWeight: '600' },
  footer: {
    marginTop: '48px', paddingTop: '20px', borderTop: '1px solid #e5e0d5',
    fontSize: '14px', color: '#6b6b7a',
  },
};
